import { useEffect } from 'react';
import type { ZzzfunConfig } from '@/hooks/useZzzfunConfig';

interface ZzzfunDownloadModalProps {
  open: boolean;
  onClose: () => void;
  config: ZzzfunConfig;
}

const zzzfunPlatforms = [
  { key: 'android', label: 'Android', desc: '安卓手机 / 平板', icon: 'ri-android-fill', tag: '推荐' },
  { key: 'ios', label: 'iOS', desc: 'iPhone / iPad', icon: 'ri-apple-fill', tag: '' },
  { key: 'harmonyos', label: 'HarmonyOS', desc: '华为鸿蒙设备', icon: 'ri-smartphone-line', tag: '' },
  { key: 'windows', label: 'Windows', desc: 'Windows 电脑版', icon: 'ri-windows-fill', tag: '' },
] as const;

export default function ZzzfunDownloadModal({ open, onClose, config }: ZzzfunDownloadModalProps) {
  useEffect(() => {
    if (!open) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-6">
      <button
        type="button"
        aria-label="关闭下载弹窗"
        className="absolute inset-0 cursor-pointer bg-foreground-950/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="zzzfun-fade-up relative w-full max-w-xl overflow-hidden rounded-2xl border border-background-200/70 bg-background-50 p-6 md:p-8">
        <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-primary-100/70 blur-2xl" />
        <div className="relative flex items-start justify-between gap-4">
          <div>
            <h3 className="font-heading text-2xl text-foreground-950 md:text-3xl">下载ZzzFun客户端</h3>
            <p className="mt-2 text-sm text-foreground-600">
              选择你的设备系统，立即体验ZzzFun官方APP免费下载，多端同步追番进度。
            </p>
          </div>
          <button
            type="button"
            aria-label="关闭"
            onClick={onClose}
            className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full bg-background-100 text-foreground-700 transition-colors hover:bg-background-200 hover:text-foreground-950"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="relative mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {zzzfunPlatforms.map((platform) => (
            <a
              key={platform.key}
              href={config.download[platform.key]}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex cursor-pointer items-center gap-4 rounded-xl border border-background-200/70 bg-background-100 p-4 transition-colors hover:border-primary-300 hover:bg-primary-50"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-500 text-background-50 dark:text-foreground-950">
                <i className={`${platform.icon} text-2xl`} />
              </span>
              <span className="flex-1">
                <span className="flex items-center gap-2">
                  <span className="text-base font-bold text-foreground-950">{platform.label}</span>
                  {platform.tag ? (
                    <span className="rounded-full bg-accent-100 px-2 py-0.5 text-[10px] font-bold text-accent-700">
                      {platform.tag}
                    </span>
                  ) : null}
                </span>
                <span className="mt-0.5 block text-xs text-foreground-600">{platform.desc}</span>
              </span>
              <i className="ri-download-2-line text-lg text-foreground-500 transition-colors group-hover:text-primary-600" />
            </a>
          ))}
        </div>

        <p className="relative mt-5 flex items-center gap-2 text-xs text-foreground-600">
          <i className="ri-shield-check-line text-base text-primary-600" />
          官方正版下载通道，安装包安全无捆绑，点击后将前往ZzzFun.net下载页。
        </p>
      </div>
    </div>
  );
}